/**
 * Channel Input Block Renderer
 *
 * The `ui_channel_input` card: a "Delivers to" checklist with Strive Inbox
 * (always on), Google Chat, and Slack. Checking a connected provider expands
 * its destination pickers (Slack: workspace + channels; Google Chat: spaces),
 * reusing the Routines page plumbing (`useChannelPickerState` — rows, chips,
 * Slack invite) and the shared `DestinationCombobox`. Unconnected providers
 * show an inline "+ Connect" that runs the OAuth popup right here (via
 * `useConnectionFlow`) — no settings-page detour; the row unlocks reactively
 * when the credential appears.
 *
 * The LLM only passes intent-level `preselect` hints (provider flags +
 * channel/space NAMES the user actually said); this renderer resolves names
 * against the live lists once they load. Confirm emits the wire-shaped
 * `output_channels` (via `toOutputChannels`) plus a human `summary`.
 */

import React from "react";
import { Inbox, Loader2, Mail, Plus } from "lucide-react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { Checkbox, cn } from "@strivelabs/ui-core";
import { Button } from "@strivelabs/ui-core/v2";
import { AppLogo } from "@/components/AppLogo";
import { ROUTINE_EMAIL_DELIVERY_FLAG } from "@/components/routines/featureFlag";
import { useApps } from "@/hooks/useApps";
import { useCredentials } from "@/hooks/useConnections";
import { useConnectionFlow } from "@/hooks/useConnectionFlow";
import { useChannelPickerState } from "@/components/routines/RoutineChannelPicker";
import { EmailRecipientsInput, isValidEmail } from "@/components/routines/EmailRecipientsInput";
import { DestinationCombobox, credLabel } from "@/components/routines/ProviderPicker";
import { fromOutputChannels, toOutputChannels } from "@/utils/routineOutputChannels";
import type { RoutineChannelValue } from "@/utils/routineOutputChannels";
import type { ConnectionsCardData } from "@/types/connections";
import type { UIBlock, UIActionEvent, ChannelInputContent } from "@/types/ui-blocks";

const DEFAULT_TITLE = "Where should I notify you?";
const DEFAULT_DESCRIPTION = "Choose where alerts should be delivered, or describe it in your own words.";

type ProviderKey = "slack" | "google_chat";

/**
 * Human list of selected destinations for the confirm summary. Uses resolved
 * chip labels once every chip has loaded; while any label is still resolving
 * (fetch in flight), falls back to a count so the summary never shows raw ids
 * or empty parentheses.
 */
function summarizeChips(
  chips: { label: string; loading: boolean }[],
  count: number,
  noun: string
): string {
  const allResolved = chips.every((chip) => !chip.loading);
  if (allResolved && chips.length > 0) {
    return chips.map((chip) => chip.label).join(", ");
  }
  return `${count} ${count === 1 ? noun : `${noun}s`}`;
}

export interface ChannelInputBlockRendererProps {
  block: UIBlock;
  onUIAction: (event: UIActionEvent) => void;
  /** When true, the component is disabled (historical render). */
  readOnly?: boolean;
}

/** Derive the initial controlled value / checked flags from a rehydrated submission. */
function seedFromContent(content: ChannelInputContent | null): {
  value: RoutineChannelValue;
  checked: Record<ProviderKey, boolean> & { email: boolean };
  savedEmail: boolean;
} {
  // A rehydrated submission wins (historical render of a confirmed card);
  // otherwise the routine's CURRENT selection seeds an edit flow — ids and
  // recipients verbatim, so the user edits from what is already saved.
  const seedList =
    content?.submittedChannels?.output_channels?.channels ??
    content?.preselect?.output_channels?.channels;
  const value = fromOutputChannels(seedList);
  // SAVED recipients (a submission or a stored routine selection) grandfather
  // the gated Email row; LLM preselect hints below deliberately don't.
  const savedEmail = (value.email?.recipients.filter(isValidEmail).length ?? 0) > 0;
  // Email needs no live-list resolution, so preselect addresses are applied
  // directly at seed time (the LLM only passes addresses the user said).
  const preselectEmails = (content?.preselect?.emails ?? []).filter(isValidEmail);
  if (!value.email && preselectEmails.length > 0) {
    value.email = { recipients: [...new Set(preselectEmails)] };
  }
  // Whatever the seed source, drop malformed addresses at the seam — a bad
  // stored/LLM-supplied recipient would otherwise ride through Confirm and
  // abort the routine save server-side.
  if (value.email) {
    value.email = { recipients: value.email.recipients.filter(isValidEmail) };
  }
  return {
    value,
    savedEmail,
    checked: {
      slack: Boolean(value.slack?.channels?.length) || Boolean(content?.preselect?.slack),
      google_chat: Boolean(value.gchat?.spaces?.length) || Boolean(content?.preselect?.google_chat),
      email: Boolean(value.email?.recipients?.length) || Boolean(content?.preselect?.email),
    },
  };
}

export function ChannelInputBlockRenderer({
  block,
  onUIAction,
  readOnly = false,
}: ChannelInputBlockRendererProps) {
  const content = block.type === "channel_input" ? block.content : null;
  const seed = React.useMemo(() => seedFromContent(content), [content]);

  const [value, setValue] = React.useState<RoutineChannelValue>(seed.value);
  const [checked, setChecked] = React.useState<Record<ProviderKey, boolean> & { email: boolean }>(seed.checked);
  const [submitted, setSubmitted] = React.useState<boolean>(Boolean(content?.submittedChannels));
  const [pendingConnect, setPendingConnect] = React.useState<ProviderKey | null>(null);
  const namePrefillDone = React.useRef<Record<ProviderKey, boolean>>({
    slack: false,
    google_chat: false,
  });

  const { slackProps, gchatProps } = useChannelPickerState(value, setValue);

  // Email row is feature-flagged (see ROUTINE_EMAIL_DELIVERY_FLAG). A seed
  // that already carries SAVED recipients is grandfathered so a flag-off edit
  // flow never silently drops a saved destination on Confirm.
  const emailFlagOn = useFeatureFlagEnabled(ROUTINE_EMAIL_DELIVERY_FLAG) === true;
  const emailAvailable = emailFlagOn || seed.savedEmail;

  const { data: appsData } = useApps();
  const apps = React.useMemo(() => appsData ?? [], [appsData]);
  const { handleConnectApp, isConnecting, connectingApp } = useConnectionFlow({ apps });

  const { data: credentialsData } = useCredentials();
  const slackCreds = React.useMemo(
    () =>
      ((credentialsData?.credentials ?? []) as ConnectionsCardData[]).filter(
        (cred) => cred.app_name === "slack"
      ),
    [credentialsData]
  );

  // Resolve preselect channel/space NAMES to ids once the live lists load.
  // One-shot per provider (and applied as a single atomic value write) so
  // later user edits are never overwritten.
  const preselect = content?.preselect;
  React.useEffect(() => {
    if (submitted || readOnly) return;
    const names = preselect?.slack_channel_names ?? [];
    const credId = slackProps.account?.id;
    if (names.length === 0 || namePrefillDone.current.slack || slackProps.rows.length === 0 || !credId) return;
    namePrefillDone.current.slack = true;
    const wanted = new Set(names.map((name) => name.replace(/^#\s*/, "").toLowerCase()));
    const ids = slackProps.rows
      .filter((row) => row.selectable && wanted.has(row.label.replace(/^#\s*/, "").toLowerCase()))
      .map((row) => row.value);
    if (ids.length > 0) {
      setChecked((prev) => ({ ...prev, slack: true }));
      setValue((prev) => ({
        ...prev,
        slack: {
          credential_id: prev.slack?.credential_id || credId,
          channels: [...new Set([...(prev.slack?.channels ?? []), ...ids])],
        },
      }));
    }
  }, [preselect, slackProps.rows, slackProps.account?.id, submitted, readOnly]);

  React.useEffect(() => {
    if (submitted || readOnly) return;
    const names = preselect?.google_chat_space_names ?? [];
    const credId = gchatProps.account?.id;
    if (names.length === 0 || namePrefillDone.current.google_chat || gchatProps.rows.length === 0 || !credId) return;
    namePrefillDone.current.google_chat = true;
    const wanted = new Set(names.map((name) => name.toLowerCase()));
    const ids = gchatProps.rows
      .filter((row) => wanted.has(row.label.toLowerCase()))
      .map((row) => row.value);
    if (ids.length > 0) {
      setChecked((prev) => ({ ...prev, google_chat: true }));
      setValue((prev) => ({
        ...prev,
        gchat: {
          credential_id: prev.gchat?.credential_id || credId,
          spaces: [...new Set([...(prev.gchat?.spaces ?? []), ...ids])],
        },
      }));
    }
  }, [preselect, gchatProps.rows, gchatProps.account?.id, submitted, readOnly]);

  // OAuth completes in a popup; when the credential lands, clear the pending
  // state and auto-check the provider the user just connected.
  const slackConnected = slackProps.connected;
  const gchatConnected = gchatProps.connected;
  React.useEffect(() => {
    if (!pendingConnect) return;
    const nowConnected = pendingConnect === "slack" ? slackConnected : gchatConnected;
    if (nowConnected) {
      setChecked((prev) => ({ ...prev, [pendingConnect]: true }));
      setPendingConnect(null);
      return;
    }
    // The connect flow ended (popup closed / cancelled) without a credential —
    // clear the pending spinner so the row falls back to an actionable
    // "+ Connect" instead of spinning forever. `isConnecting` turns true
    // synchronously with the click, so this only fires once the flow settles.
    if (!isConnecting) {
      setPendingConnect(null);
    }
  }, [pendingConnect, slackConnected, gchatConnected, isConnecting]);

  const connectedByProvider: Record<ProviderKey, boolean> = {
    slack: slackConnected,
    google_chat: gchatConnected,
  };

  if (block.type !== "channel_input" || !content) return null;

  const disabled = readOnly || submitted;

  const handleConnect = (provider: ProviderKey) => {
    const app = apps.find((candidate) => candidate.short_name === provider);
    if (!app) return;
    setPendingConnect(provider);
    handleConnectApp(app);
  };

  const toggleProvider = (provider: ProviderKey) => {
    // Allow UNchecking a provider even when it isn't connected: the LLM may
    // pre-check a provider the tenant hasn't connected (e.g. "notify me on
    // Slack"), and without this the checkbox would be locked-on while Confirm
    // stays gated — an inescapable card. Checking a provider ON still requires
    // a connection (use the inline Connect button).
    if (disabled || (!connectedByProvider[provider] && !checked[provider])) return;
    setChecked((prev) => ({ ...prev, [provider]: !prev[provider] }));
  };

  const slackSelectionCount = value.slack?.channels?.length ?? 0;
  const gchatSelectionCount = value.gchat?.spaces?.length ?? 0;
  const emailRecipients = value.email?.recipients ?? [];
  // With the row gated off, a checked-email state (an LLM preselect hint, say)
  // must neither block Confirm behind an invisible row nor ride into the
  // emitted selection — email simply doesn't exist for this card.
  const emailChecked = checked.email && emailAvailable;
  // A checked provider must be connected AND have a destination (email needs no
  // connection — just at least one recipient). A checked-but-unconnected
  // provider blocks Confirm (no silent drop) but is always uncheckable via
  // toggleProvider, so it can never lock the card.
  const confirmEnabled =
    (!checked.slack || (slackConnected && slackSelectionCount > 0)) &&
    (!checked.google_chat || (gchatConnected && gchatSelectionCount > 0)) &&
    (!emailChecked || emailRecipients.length > 0);

  const handleConfirm = () => {
    if (disabled || !confirmEnabled) return;
    const effective: RoutineChannelValue = {
      ...(checked.slack && value.slack ? { slack: value.slack } : {}),
      ...(checked.google_chat && value.gchat ? { gchat: value.gchat } : {}),
      ...(emailChecked && value.email ? { email: value.email } : {}),
    };
    const parts = ["Strive Inbox"];
    if (checked.slack && slackSelectionCount > 0) {
      parts.push(`Slack (${summarizeChips(slackProps.selected, slackSelectionCount, "channel")})`);
    }
    if (checked.google_chat && gchatSelectionCount > 0) {
      parts.push(`Google Chat (${summarizeChips(gchatProps.selected, gchatSelectionCount, "space")})`);
    }
    if (emailChecked && emailRecipients.length > 0) {
      parts.push(`Email (${emailRecipients.join(", ")})`);
    }
    setSubmitted(true);
    onUIAction({
      type: "ui_action",
      component: "channel_input",
      label: content.title ?? DEFAULT_TITLE,
      state: {
        output_channels: toOutputChannels(effective),
        summary: parts.join(" · "),
      },
    });
  };

  const renderConnectButton = (provider: ProviderKey, displayName: string) => {
    const isConnectingThis =
      pendingConnect === provider ||
      (isConnecting && connectingApp?.short_name === provider);
    return (
      <Button variant="secondary" size="xs"
        type="button"
        disabled={disabled || isConnectingThis}
        onClick={() => handleConnect(provider)}
      >
        {isConnectingThis ? (
          <>
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
            Connecting
          </>
        ) : (
          <>
            <Plus className="h-3 w-3" aria-hidden="true" />
            Connect
          </>
        )}
        <span className="sr-only">{displayName}</span>
      </Button>
    );
  };

  return (
    <div className="my-2 w-full rounded-lg border border-custom-border bg-[#fbfbfb] p-5">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-medium text-gray-900">{content.title ?? DEFAULT_TITLE}</h3>
        <p className="text-sm text-gray-500">{content.description ?? DEFAULT_DESCRIPTION}</p>
      </div>

      <div className="flex gap-4 py-4">
        <span className="w-24 shrink-0 pt-0.5 text-sm font-medium text-gray-500">Delivers to</span>

        <div className="flex min-w-0 flex-1 flex-col gap-2.5">
          {/* Strive Inbox — the in-app card is always created, so it's fixed on. */}
          <div className="flex h-7 items-center gap-2">
            <Checkbox checked disabled aria-label="Strive Inbox (always on)" />
            <Inbox className="h-4 w-4 text-gray-600" aria-hidden="true" />
            <span className="text-sm text-gray-900">Strive Inbox</span>
          </div>

          {/* Google Chat */}
          <div className="flex flex-col gap-2">
            <div className="flex h-7 items-center gap-2">
              <Checkbox
                checked={checked.google_chat}
                disabled={disabled || (!gchatProps.connected && !checked.google_chat)}
                onCheckedChange={() => toggleProvider("google_chat")}
                aria-label="Google Chat"
              />
              <AppLogo shortName="google_chat" displayName="Google Chat" size="sm" />
              <span className={cn("text-sm", gchatProps.connected ? "text-gray-900" : "text-gray-500")}>
                Google Chat
              </span>
              {!gchatProps.connected ? renderConnectButton("google_chat", "Google Chat") : null}
            </div>
            {checked.google_chat && gchatProps.connected ? (
              <div className="ml-6 flex items-center gap-4 border-l border-custom-border pl-4">
                <span className="w-20 shrink-0 text-sm text-gray-500">Spaces</span>
                <div className="min-w-0 flex-1">
                  <DestinationCombobox
                    appName="google_chat"
                    noun="spaces"
                    isLoading={gchatProps.isLoading}
                    rows={gchatProps.rows}
                    selected={gchatProps.selected}
                    onToggle={gchatProps.onToggle}
                    disabled={disabled}
                  />
                </div>
              </div>
            ) : null}
          </div>

          {/* Slack */}
          <div className="flex flex-col gap-2">
            <div className="flex h-7 items-center gap-2">
              <Checkbox
                checked={checked.slack}
                disabled={disabled || (!slackProps.connected && !checked.slack)}
                onCheckedChange={() => toggleProvider("slack")}
                aria-label="Slack"
              />
              <AppLogo shortName="slack" displayName="Slack" size="sm" />
              <span className={cn("text-sm", slackProps.connected ? "text-gray-900" : "text-gray-500")}>
                Slack
              </span>
              {!slackProps.connected ? renderConnectButton("slack", "Slack") : null}
            </div>
            {checked.slack && slackProps.connected ? (
              <div className="ml-6 flex flex-col gap-2 border-l border-custom-border pl-4">
                {slackCreds.length > 0 ? (
                  <div className="flex items-center gap-4">
                    <span className="w-20 shrink-0 text-sm text-gray-500">Workspace</span>
                    <span className="truncate text-sm text-gray-900">
                      {credLabel(slackProps.account ?? slackCreds[0])}
                    </span>
                  </div>
                ) : null}
                <div className="flex items-center gap-4">
                  <span className="w-20 shrink-0 text-sm text-gray-500">Channels</span>
                  <div className="min-w-0 flex-1">
                    <DestinationCombobox
                      appName="slack"
                      noun="channels"
                      isLoading={slackProps.isLoading}
                      rows={slackProps.rows}
                      selected={slackProps.selected}
                      onToggle={slackProps.onToggle}
                      disabled={disabled}
                    />
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {/* Email — no connection needed; the platform sends it. Gated (see
              emailAvailable above); grandfathered seeds keep the row. */}
          {emailAvailable ? (
            <div className="flex flex-col gap-2">
              <div className="flex h-7 items-center gap-2">
                <Checkbox
                  checked={checked.email}
                  disabled={disabled}
                  onCheckedChange={() =>
                    setChecked((prev) => ({ ...prev, email: !prev.email }))
                  }
                  aria-label="Email"
                />
                <Mail className="h-4 w-4 text-gray-600" aria-hidden="true" />
                <span className="text-sm text-gray-900">Email</span>
              </div>
              {checked.email ? (
                <div className="ml-6 flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2 border-l border-custom-border pl-3">
                  <span className="w-20 shrink-0 text-sm text-gray-500">Recipients</span>
                  <EmailRecipientsInput
                    recipients={emailRecipients}
                    disabled={disabled}
                    onChange={(recipients) =>
                      setValue((prev) => ({ ...prev, email: { recipients } }))
                    }
                  />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {!disabled ? (
        <div className="flex justify-end">
          <Button size="sm" disabled={!confirmEnabled} onClick={handleConfirm}>
            Confirm
          </Button>
        </div>
      ) : null}
    </div>
  );
}
